import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { useDashboardFilters } from '@/contexts/DashboardFilterContext';
import { Card, Typography, Box, CircularProgress } from '@mui/material';
import axiosInstance from '@/utils/axios';
import { SUPPORTED_SPORTS } from 'constants/globalConstants';

// ─── Tipi ───────────────────────────────────────────────────────────────────

interface ChartDataPoint {
  date: string;
  content_type: string; // "KEY_MOMENT" | "CLIP" | "MATCH_HIGHLIGHT" | "GOAL_PARADE"
  value: number;
}

// ─── Costanti ────────────────────────────────────────────────────────────────
const CURRENT_SPORT = ((import.meta as any).env.VITE_APP_SPORT).toUpperCase();
const SERIES_CONFIG: Record<string, { label: string; color: string }> = {
  KEY_MOMENT:      { label: 'Key Moment',  color: '#dc2626' },
  CLIP:            { label: 'Clip',        color: '#ea580c' },
  MATCH_HIGHLIGHT: { label: 'Highlight',   color: '#eab308' },
  ...(CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && {GOAL_PARADE:     { label: 'Goal Parade', color: '#e5e7eb' }}),
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildFilterBody(
  contentType: string[],
  competition: string[],
  season: string[],
  dateRange: any
) {
  const clean   = (arr: string[]) => arr.filter((el) => el !== 'All');
  const toParam = (arr: string[]) => (arr.length === 1 ? arr[0] : arr);
  return {
    contentType: toParam(clean(contentType)),
    competition: toParam(clean(competition)),
    season:      toParam(clean(season)),
    startDate:   dateRange?.[0]?.format?.('YYYY-MM-DD') ?? dateRange?.[0] ?? null,
    endDate:     dateRange?.[1]?.format?.('YYYY-MM-DD') ?? dateRange?.[1] ?? null,
  };
}

function buildSeries(
  raw: ChartDataPoint[],
  xAxisData: string[],
  activeContentTypes: string[]
): echarts.BarSeriesOption[] {
  const grouped: Record<string, Record<string, number>> = {};
  raw.forEach(({ date, content_type, value }) => {
    const key = content_type.toUpperCase().trim();
    if (!grouped[key]) grouped[key] = {};
    grouped[key][date] = (grouped[key][date] ?? 0) + value;
  });

  return Object.entries(SERIES_CONFIG)
    .filter(([key]) => activeContentTypes.includes('All') || activeContentTypes.includes(key))
    .map(([key, { label, color }]) => ({
      name: label,
      type: 'bar' as const,
      stack: 'total_content',
      emphasis: { focus: 'series' as const },
      barMaxWidth: 40,
      itemStyle: { color },
      data: xAxisData.map((date) => grouped[key]?.[date] ?? 0),
    }));
}

function applyChartOption(
  node: HTMLDivElement,
  instanceRef: React.MutableRefObject<echarts.ECharts | null>,
  chartData: ChartDataPoint[],
  contentType: string[]
) {
  // Dispose se il nodo è cambiato (succede dopo il toggle loading→chart)
  if (instanceRef.current && instanceRef.current.getDom() !== node) {
    instanceRef.current.dispose();
    instanceRef.current = null;
  }
  if (!instanceRef.current) {
    instanceRef.current = echarts.init(node);
  }

  const xAxisData = Array.from(new Set(chartData.map((d) => d.date))).sort();
  const filteredSeries = buildSeries(chartData, xAxisData, contentType);

  instanceRef.current.setOption(
    {
      animationDuration: 500,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const items = (Array.isArray(params) ? params : [params]) as any[];
          const header = `<div style="margin-bottom:6px;font-weight:600">${items[0]?.axisValueLabel ?? ''}</div>`;
          const rows = items
            .filter((p) => p.value !== 0)
            .map((p) => {
              const dot = `<span style="
                display:inline-block;
                width:10px;height:10px;border-radius:50%;
                background:${p.color};
                border:0.5px solid #000;
                margin-right:6px;vertical-align:middle;
              "></span>`;
              return `<div style="display:flex;align-items:center;gap:4px;margin:2px 0">
                ${dot}
                <span style="flex:1">${p.seriesName}</span>
                <span style="font-weight:600;margin-left:12px">${p.value}</span>
              </div>`;
            })
            .join('');
          return header + rows;
        },
      },
      legend: {
        top: 0,
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 25,
        textStyle: {
          color: '#ffffff',
          fontSize: 15
        },
        inactiveColor: '#666666',
        data: filteredSeries.map((s) => s.name as string),
      },
      grid: { top: 55, left: '1%', right: '1%', bottom: '3%', containLabel: true },
      xAxis: {
        type: 'category', data: xAxisData, boundaryGap: true,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
        axisLabel: { color: '#9ca3af', fontSize: 11, interval: 'auto' },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false }, axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { color: '#9ca3af', fontSize: 11 },
      },
      series: filteredSeries,
    },
    true // notMerge: rimpiazza l'opzione per intero ad ogni update
  );
}

// ─── Componente ──────────────────────────────────────────────────────────────

export default function ContentTypeStackedChart() {
  const chartRef     = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceRef  = useRef<echarts.ECharts | null>(null);

  const { contentType, competition, season, dateRange, updateDashboard, filterLoaded } = useDashboardFilters();

  const filtersRef = useRef({ contentType, competition, season, dateRange });
  filtersRef.current = { contentType, competition, season, dateRange };
  const [appliedContentType, setAppliedContentType] = useState<string[]>(contentType ?? []);

  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);

  // ── 1. Fetch: SOLO su updateDashboard / filterLoaded ──────────────────────
  useEffect(() => {
    if (filterLoaded) {
      let cancelled = false;

      const fetchData = async () => {
        try {
          setIsLoading(true);
          setError(null);

          const {
            contentType: ct,
            competition: comp,
            season: seas,
            dateRange: dr,
          } = filtersRef.current;

          const body = buildFilterBody(ct ?? [], comp ?? [], seas ?? [], dr);
          const { data } = await axiosInstance.post<ChartDataPoint[]>(
            '/dashboard/getStackedBarChart',
            body
          );

          if (!cancelled) {
            setChartData(Array.isArray(data) ? data : []);
            // "Congela" anche il contentType usato per costruire le series
            setAppliedContentType(ct ?? []);
          }
        } catch (err) {
          if (!cancelled)
            setError(err instanceof Error ? err.message : 'Failed to load analytics data');
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      fetchData();
      return () => { cancelled = true; };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDashboard, filterLoaded]);

  useEffect(() => {
    if (isLoading || error || !chartRef.current) return;
    applyChartOption(chartRef.current, instanceRef, chartData, appliedContentType);
  }, [chartData, appliedContentType, isLoading, error]);

  // ── 3. Resize ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const resize = () => instanceRef.current?.resize();
    const observer = new ResizeObserver(resize);
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      observer.disconnect();
    };
  }, []);

  // ── 4. Dispose ────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      instanceRef.current?.dispose();
      instanceRef.current = null;
    };
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }} data-tour={"dashboard_daily_stats_content_type_card"}>
      {/* Header Unificato della Card */}
      <Box px={3} pt={2.5} pb={2}>
        <Typography variant="h3" fontWeight={700} color="text.primary" sx={{ fontSize: '1.25rem' }}>
          Daily Video Trend by Content Type
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Overview of daily video production volume and content categories
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={380}>
          <CircularProgress size={40} />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={380}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Box>
      ) : !chartData || chartData.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={380}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            No data available to display
          </Typography>
        </Box>
      ) : (
        <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: '350px' }} />
      )}
    </Card>
  );
}